import React, { useEffect, useState } from 'react';
import { adminService } from '../../services/adminService';
import { AuditLog } from '../../types';

export const AdminAuditLogs: React.FC = () => {
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [entityFilter, setEntityFilter] = useState('');
  const [actionFilter, setActionFilter] = useState('');

  useEffect(() => { loadLogs(); }, []);

  const loadLogs = async () => {
    setIsLoading(true);
    try {
      const data = await adminService.getAuditLogs({
        search: search || undefined,
        entity_type: entityFilter || undefined,
        action: actionFilter || undefined,
      });
      setLogs(data.results ?? []);
      setTotal(data.count ?? 0);
    } catch {} finally {
      setIsLoading(false);
    }
  };

  const ENTITY_TYPES = ['', 'ServiceRequest', 'Deal', 'Negotiation', 'SurveyorProfile', 'User'];
  const ACTIONS = ['', 'created', 'updated', 'approved', 'rejected', 'assigned', 'confirmed', 'closed'];

  return (
    <div>
      <h1 style={s.title}>Audit Logs ({total})</h1>

      {/* Filters */}
      <div style={s.filterRow}>
        <input style={s.input} placeholder="Search action, actor…" value={search}
          onChange={(e) => setSearch(e.target.value)} />
        <select style={s.select} value={entityFilter} onChange={(e) => setEntityFilter(e.target.value)}>
          {ENTITY_TYPES.map((e) => <option key={e} value={e}>{e || 'All Entities'}</option>)}
        </select>
        <select style={s.select} value={actionFilter} onChange={(e) => setActionFilter(e.target.value)}>
          {ACTIONS.map((a) => <option key={a} value={a}>{a || 'All Actions'}</option>)}
        </select>
        <button style={s.searchBtn} onClick={loadLogs}>Search</button>
      </div>

      {isLoading ? (
        <div style={s.center}><div style={s.spinner} /></div>
      ) : logs.length === 0 ? (
        <div style={s.empty}>No audit logs found.</div>
      ) : (
        <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
          <div style={s.table}>
            <div style={s.thead}>
              {[{ label: 'Actor', flex: 1.5 }, { label: 'Action', flex: 1 }, { label: 'Entity', flex: 1.2 }, { label: 'ID', flex: 1 }, { label: 'Date', flex: 1.3 }].map(h => (
                <div key={h.label} style={{ ...s.th, flex: h.flex }}>{h.label}</div>
              ))}
            </div>
            {logs.map((log) => (
              <div key={log.id} style={s.row}>
                <div style={{ ...s.td, flex: 1.5, overflow: 'hidden' }}>
                  <div style={{ fontWeight: 600, color: 'var(--sc-text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{log.actor?.first_name} {log.actor?.last_name}</div>
                  <div style={{ fontSize: 11, color: 'var(--sc-text-tertiary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{log.actor?.role}</div>
                </div>
                <div style={{ ...s.td, flex: 1 }}><span style={s.actionBadge}>{log.action}</span></div>
                <div style={{ ...s.td, flex: 1.2, color: 'var(--sc-text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{log.entity_type}</div>
                <div style={{ ...s.td, flex: 1, color: 'var(--sc-text-tertiary)', fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{log.entity_id?.slice(0, 12)}…</div>
                <div style={{ ...s.td, flex: 1.3, fontSize: 12, color: 'var(--sc-text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {new Date(log.timestamp).toLocaleString('en-ZW')}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const s: Record<string, React.CSSProperties> = {
  title: { fontSize: 26, fontWeight: 800, color: 'var(--sc-text-primary)', margin: '0 0 16px', fontFamily: "'Outfit', sans-serif" },
  filterRow: { display: 'flex', gap: 12, marginBottom: 16, alignItems: 'center', flexWrap: 'wrap' },
  input: { padding: '10px 14px', border: '1px solid var(--sc-border)', borderRadius: 10, fontSize: 14, flex: 1, minWidth: 200, outline: 'none', background: 'var(--sc-bg-input)', color: 'var(--sc-text-primary)' },
  select: { padding: '10px 14px', border: '1px solid var(--sc-border)', borderRadius: 10, fontSize: 13, background: 'var(--sc-bg-secondary)', color: 'var(--sc-text-primary)', outline: 'none', minWidth: 150 },
  searchBtn: { padding: '10px 20px', background: 'var(--sc-brand-primary)', color: 'var(--sc-text-inverse)', border: 'none', borderRadius: 10, fontWeight: 700, cursor: 'pointer', fontSize: 13, minHeight: 44, transition: 'opacity 0.2s' },
  table: { background: 'var(--sc-bg-secondary)', borderRadius: 14, border: '1px solid var(--sc-border)', overflow: 'hidden', boxShadow: 'var(--sc-shadow-md)', minWidth: 600 },
  thead: { display: 'flex', padding: '10px 20px', background: 'var(--sc-bg-primary)', borderBottom: '1px solid var(--sc-border)' },
  th: { flex: 1, fontSize: 11, fontWeight: 700, color: 'var(--sc-text-secondary)', textTransform: 'uppercase' as const },
  row: { display: 'flex', padding: '13px 20px', borderBottom: '1px solid var(--sc-border)', alignItems: 'center', transition: 'background 0.2s' },
  td: { flex: 1, fontSize: 13, color: 'var(--sc-text-primary)' },
  actionBadge: { padding: '3px 10px', background: 'var(--sc-brand-primary-bg)', color: 'var(--sc-brand-primary)', borderRadius: 6, fontSize: 11, fontWeight: 600, display: 'inline-block' },
  center: { display: 'flex', justifyContent: 'center', padding: 60 },
  spinner: { width: 32, height: 32, border: '3px solid var(--sc-border)', borderTopColor: 'var(--sc-brand-primary)', borderRadius: '50%', animation: 'spin 0.8s linear infinite' },
  empty: { textAlign: 'center' as const, padding: 60, color: 'var(--sc-text-secondary)', background: 'var(--sc-bg-secondary)', borderRadius: 14, border: '1px solid var(--sc-border)', boxShadow: 'var(--sc-shadow-md)' },
};

